import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal — Reusable modal overlay
 * Used by: StillThereModal, ConsentTextPopup, ContactFormModal, T&C popup
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen       - Controls visibility
 * @param {Function} props.onClose      - Called on X click or backdrop click (if closable)
 * @param {string}   props.title        - Modal title text
 * @param {string}   props.superTitle   - Small text above title (e.g. "Get in touch")
 * @param {boolean}  props.closable     - Show X button and allow backdrop close (default: true)
 * @param {string}   props.size         - 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * @param {ReactNode} props.children    - Modal body content
 * @param {ReactNode} props.footer      - Footer content (buttons)
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  superTitle,
  closable = true,
  size = 'md',
  children,
  footer,
}) {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Trap focus inside modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && closable) {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closable, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
  };

  const handleBackdropClick = (e) => {
    if (closable && e.target === overlayRef.current) {
      onClose?.();
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-carbon-black/50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className={`
          relative bg-white shadow-xl w-full mx-4
          max-h-[90vh] flex flex-col
          ${sizeClasses[size] || sizeClasses.md}
        `}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex-shrink-0">
          {superTitle && (
            <p className="text-sm text-n5 mb-1">{superTitle}</p>
          )}
          <div className="flex items-start justify-between">
            <h2 id="modal-title" className="font-sandvik-headline font-bold text-xl text-carbon-black">
              {title}
            </h2>
            {closable && (
              <button
                onClick={onClose}
                className="text-n5 hover:text-carbon-black transition-colors ml-4 -mt-1"
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-4 overflow-y-auto flex-1">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 border-t border-n2 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
